import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, X, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PaymentCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Parse payment response from URL parameters
    const data = {
      mdstatus: searchParams.get('mdstatus'),
      procreturncode: searchParams.get('procreturncode'),
      response: searchParams.get('response'),
      errmsg: searchParams.get('errmsg'),
      authcode: searchParams.get('authcode'),
      oid: searchParams.get('oid'),
      txnamount: searchParams.get('txnamount'),
      mderrormessage: searchParams.get('mderrormessage'),
    };

    setPaymentData(data);
    setLoading(false);
  }, [searchParams]);

  const isSuccessful = () => {
    if (!paymentData) return false;
    return (
      ['1', '2', '3', '4'].includes(paymentData.mdstatus) &&
      paymentData.procreturncode === '00' &&
      paymentData.response === 'Approved'
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">İşlem sonucu yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (isSuccessful()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Ödeme Başarılı!</h1>
            <p className="text-gray-600 mb-6">
              İşleminiz başarıyla tamamlandı.
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">İşlem No:</span>
                <span className="font-semibold text-gray-900">{paymentData.oid}</span>
              </div>
              {paymentData.authcode && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Onay Kodu:</span>
                  <span className="font-semibold text-gray-900">{paymentData.authcode}</span>
                </div>
              )}
              {paymentData.txnamount && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tutar:</span>
                  <span className="font-semibold text-gray-900">
                    {(parseFloat(paymentData.txnamount) / 100).toFixed(2)} TRY
                  </span>
                </div>
              )}
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800">
                Ödeme onayınız e-posta adresinize gönderilecektir.
              </p>
            </div>

            <Button
              onClick={() => navigate('/')}
              className="w-full"
              size="lg"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Ana Sayfaya Dön
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Payment failed
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-rose-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-12 h-12 text-red-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Ödeme Başarısız</h1>
          <p className="text-gray-600 mb-6">
            Ödeme işleminiz tamamlanamadı.
          </p>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <p className="text-sm font-semibold text-red-900 mb-1">Hata Detayı:</p>
                <p className="text-sm text-red-800">
                  {paymentData.errmsg || paymentData.mderrormessage || 'İşlem tamamlanamadı'}
                </p>
                {paymentData.procreturncode && paymentData.procreturncode !== '00' && (
                  <p className="text-xs text-red-700 mt-2">
                    Hata Kodu: {paymentData.procreturncode}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="w-full"
              size="lg"
            >
              Tekrar Dene
            </Button>
            <Button
              onClick={() => navigate('/')}
              className="w-full"
              size="lg"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Ana Sayfaya Dön
            </Button>
          </div>

          <p className="text-xs text-gray-500 mt-6">
            Sorun devam ederse lütfen bizimle iletişime geçin.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentCallbackPage;
